import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import StructuredData from "@/components/StructuredData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  AlertTriangle, 
  Shield, 
  TrendingUp, 
  CheckCircle2,
  Clock,
  Euro,
  FileText,
  ArrowLeft,
  Users
} from "lucide-react";
import { getCaseStudyBySlug, caseStudies } from "@/utils/caseStudiesData";

const SchadensfallPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const caseStudy = getCaseStudyBySlug(slug || "");

  if (!caseStudy) {
    return <Navigate to="/cyber-risiken" replace />;
  }

  const breadcrumbItems = [
    { label: "Cyber-Risiken", url: "/cyber-risiken" },
    { label: "Schadensfälle", url: "/cyber-risiken#schadensfaelle" },
    { label: caseStudy.title }
  ];

  const categoryColors: Record<string, string> = {
    ransomware: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-200",
    dsgvo: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200",
    phishing: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-200",
    datenverlust: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200",
    ddos: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-200"
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{caseStudy.title} | Real-Case Schadensfall | Praxis Versichert</title>
        <meta name="description" content={caseStudy.summary} />
        <link rel="canonical" href={`https://praxis-versichert.de/schadensfall/${caseStudy.slug}`} />
      </Helmet>

      <StructuredData data={{
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": caseStudy.title,
        "description": caseStudy.summary,
        "author": {
          "@type": "Organization",
          "name": "Praxis Versichert"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Praxis Versichert",
          "logo": {
            "@type": "ImageObject",
            "url": "https://praxis-versichert.de/logo.png"
          }
        },
        "datePublished": caseStudy.date,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://praxis-versichert.de/schadensfall/${caseStudy.slug}`
        }
      }} />

      <Navigation />
      
      <main className="flex-grow">
        <div className="container py-8">
          <Button variant="ghost" size="sm" asChild className="mb-6">
            <Link to="/cyber-risiken">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zu Cyber-Risiken
            </Link>
          </Button>

          <PageBreadcrumbs items={breadcrumbItems} />

          <article className="max-w-4xl mx-auto mt-8">
            {/* Header */}
            <header className="mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className={categoryColors[caseStudy.category] || "bg-gray-500/10"}>
                  {caseStudy.category.charAt(0).toUpperCase() + caseStudy.category.slice(1)}
                </Badge>
                <Badge variant="outline">
                  {caseStudy.practiceType}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{caseStudy.title}</h1>
              <p className="text-xl text-muted-foreground leading-relaxed border-l-4 border-primary pl-6 py-2">
                {caseStudy.summary}
              </p>
            </header>

            <Separator className="my-8" />

            {/* Key Metrics Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-destructive/20 bg-destructive/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Euro className="h-4 w-4" />
                    Gesamtschaden
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{caseStudy.totalDamage}</div>
                </CardContent>
              </Card>

              <Card className="border-orange-500/20 bg-orange-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Ausfallzeit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{caseStudy.downtime}</div>
                </CardContent>
              </Card>

              <Card className="border-blue-500/20 bg-blue-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Betroffene
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{caseStudy.affectedPatients} Patienten</div>
                </CardContent>
              </Card>
            </div>

            {/* Incident Timeline */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  Vorfall-Chronologie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/90 leading-relaxed whitespace-pre-line">
                  {caseStudy.incident}
                </p>
              </CardContent>
            </Card>

            {/* Cost Breakdown */}
            <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="h-5 w-5 text-primary" />
                  Kosten-Aufschlüsselung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {caseStudy.costs.map((cost, idx) => (
                    <div key={idx} className="flex justify-between items-start pb-3 border-b last:border-0">
                      <div className="flex-1">
                        <div className="font-semibold text-foreground">{cost.category}</div>
                        <div className="text-sm text-muted-foreground mt-1">{cost.description}</div>
                      </div>
                      <div className="text-lg font-bold text-primary ml-4">{cost.amount}</div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-4 border-t-2 border-primary/30">
                    <div className="text-lg font-bold">Gesamtschaden</div>
                    <div className="text-2xl font-bold text-destructive">{caseStudy.totalDamage}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Insurance Coverage */}
            <Card className="mb-8 bg-green-500/10 border-green-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Versicherungsleistung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Versicherte Tarif</div>
                    <div className="text-xl font-bold text-green-600">{caseStudy.insuranceCoverage.provider}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Deckungssumme</div>
                    <div className="text-lg font-semibold">{caseStudy.insuranceCoverage.coverage}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-3">Übernommene Leistungen</div>
                    <ul className="space-y-2">
                      {caseStudy.insuranceCoverage.coveredItems.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="text-sm text-muted-foreground mb-2">Erstattete Summe</div>
                    <div className="text-2xl font-bold text-green-600">{caseStudy.insuranceCoverage.payout}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lessons Learned */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Lessons Learned & Präventionsmaßnahmen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {caseStudy.lessonsLearned.map((lesson, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/90">{lesson}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Recommended Tariffs */}
            <Card className="mb-8 bg-accent/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Empfohlene Tarife für ähnliche Praxen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {caseStudy.recommendedTariffs.map((tariff, idx) => (
                    <Link 
                      key={idx} 
                      to={`/tarifwissen/${tariff.slug}`}
                      className="p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-all"
                    >
                      <div className="font-semibold text-primary mb-1">{tariff.provider}</div>
                      <div className="text-sm text-muted-foreground mb-2">{tariff.reason}</div>
                      <div className="text-xs text-muted-foreground">ab {tariff.pricing}</div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Related Cases */}
            {caseStudy.relatedCases.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle>Ähnliche Schadensfälle</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {caseStudy.relatedCases.map((relatedSlug) => {
                      const related = caseStudies.find(c => c.slug === relatedSlug);
                      if (!related) return null;
                      return (
                        <Link
                          key={relatedSlug}
                          to={`/schadensfall/${relatedSlug}`}
                          className="p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-all"
                        >
                          <div className="font-semibold mb-1">{related.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">{related.summary}</div>
                        </Link>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </article>

          {/* CTA Section */}
          <section className="max-w-4xl mx-auto mt-16 p-8 rounded-lg bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border text-center">
            <h2 className="text-2xl font-bold mb-4">
              Schützen Sie Ihre Praxis vor ähnlichen Vorfällen
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Lassen Sie sich von unseren Cyber-Experten zur optimalen Absicherung beraten. 
              Wir analysieren Ihre Risiken und finden den passenden Schutz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/termin-buchen">
                  Kostenlose Beratung vereinbaren
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/cyberversicherung/vergleichsrechner">
                  Versicherungsschutz prüfen
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <FloatingContactWidget />
    </div>
  );
};

export default SchadensfallPage;
